/**
 * ImmersionPlanCard — surfaces the FULL details of a candidate's currently
 * scheduled immersion on their profile page. Replaces the generic
 * "Programmer immersion" button with a contextual block that shows :
 *
 *   • Date & heure (formatted in French, Paris time)
 *   • Lieu (full address)
 *   • Référent assigné (nom + téléphone — tap to call)
 *   • Statut de confirmation (lien vu / confirmé / pas encore)
 *   • Magic-link visible to the recruiter (click-to-copy)
 *   • Two big actions : Reprogrammer / Annuler
 *
 * Designed to be the intuitive replacement for the generic schedule button
 * once the candidate is at status === "immersion_scheduled".
 */
import { motion } from "framer-motion";
import {
    Calendar as CalendarIcon,
    CheckCircle2,
    ChevronRight,
    Clock,
    Copy,
    Eye,
    MapPin,
    Phone,
    RefreshCw,
    ThumbsDown,
    ThumbsUp,
    User2,
    XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "../../lib/api";


export default function ImmersionPlanCard({ candidate, onReschedule, onCancel, onDecide }) {
    const [referent, setReferent] = useState(null);

    useEffect(() => {
        if (!candidate.immersion_referent_id) {
            setReferent(null);
            return;
        }
        let alive = true;
        api.get("/users/team")
            .then(({ data }) => {
                if (!alive) return;
                const all = [...(data.referents || []), ...(data.pre_recruteurs || []), ...(data.admins || [])];
                setReferent(all.find((u) => u.id === candidate.immersion_referent_id) || null);
            })
            .catch(() => {});
        return () => { alive = false; };
    }, [candidate.immersion_referent_id]);

    const when = candidate.immersion_scheduled_at ? new Date(candidate.immersion_scheduled_at) : null;
    const dateLabel = when
        ? when.toLocaleDateString("fr-FR", { timeZone: "Europe/Paris", weekday: "long", day: "numeric", month: "long", year: "numeric" })
        : "—";
    const timeLabel = when
        ? when.toLocaleTimeString("fr-FR", { timeZone: "Europe/Paris", hour: "2-digit", minute: "2-digit" })
        : "—";

    // Confirmation badge
    let confirmTone = "bg-amber-50 text-amber-800 border-amber-200";
    let confirmIcon = Clock;
    let confirmLabel = "En attente du candidat";
    if (candidate.immersion_confirmed_at) {
        confirmTone = "bg-emerald-50 text-emerald-800 border-emerald-200";
        confirmIcon = CheckCircle2;
        confirmLabel = "Candidat a confirmé sa venue";
    } else if (candidate.immersion_link_viewed_at) {
        confirmTone = "bg-sky-50 text-sky-800 border-sky-200";
        confirmIcon = Eye;
        confirmLabel = "Candidat a ouvert le lien (pas encore confirmé)";
    }
    const ConfirmIcon = confirmIcon;

    const magicLink = candidate.immersion_confirm_token
        ? `${window.location.origin}/i/${candidate.immersion_confirm_token}`
        : null;

    async function copyLink() {
        if (!magicLink) return;
        try {
            await navigator.clipboard.writeText(magicLink);
            toast.success("Lien magique copié");
        } catch (e) {
            toast.error("Impossible de copier le lien");
        }
    }

    // Did the immersion time already pass? Used to surface the decision
    // buttons (Valider / Refuser) once the immersion has happened.
    const isPast = when ? when.getTime() < Date.now() : false;

    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-3xl border-2 border-shariti-orange/30 bg-gradient-to-br from-shariti-orange-soft/40 to-white p-5 md:p-6 shadow-lg"
            data-testid="immersion-plan-card"
        >
            <header className="flex items-start justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-2xl bg-shariti-orange text-white inline-flex items-center justify-center shrink-0 shadow-btn">
                        <CalendarIcon className="h-5 w-5" />
                    </div>
                    <div>
                        <div className="text-[10px] font-extrabold uppercase tracking-widest text-shariti-orange">
                            Immersion programmée
                        </div>
                        <h3 className="text-base font-extrabold text-shariti-black mt-0.5">
                            {dateLabel} · <span className="tabular-nums">{timeLabel}</span>
                        </h3>
                    </div>
                </div>
                <div className={`inline-flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full border ${confirmTone}`}>
                    <ConfirmIcon className="h-3 w-3" /> {confirmLabel}
                </div>
            </header>

            {/* Detail grid */}
            <div className="grid md:grid-cols-2 gap-3 mb-4">
                <DetailRow
                    icon={MapPin}
                    label="Lieu de l'immersion"
                    value={candidate.immersion_location || "Non renseigné"}
                    testId="plan-location"
                />
                <DetailRow
                    icon={User2}
                    label="Référent terrain"
                    value={
                        referent ? (
                            <div className="flex flex-col">
                                <span>{referent.name}</span>
                                {referent.phone && (
                                    <a
                                        href={`tel:${referent.phone.replace(/\s/g, "")}`}
                                        className="text-xs font-bold text-emerald-700 hover:underline inline-flex items-center gap-1 mt-0.5"
                                    >
                                        <Phone className="h-3 w-3" /> {referent.phone}
                                    </a>
                                )}
                            </div>
                        ) : (
                            <span className="text-amber-700">À assigner</span>
                        )
                    }
                    testId="plan-referent"
                />
            </div>

            {/* Magic link box */}
            {magicLink && (
                <div className="rounded-xl bg-white border border-shariti-gray p-3 mb-4">
                    <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1.5">
                        Lien magique de confirmation
                    </div>
                    <div className="flex items-center gap-2">
                        <code
                            className="flex-1 text-xs font-mono text-slate-700 truncate bg-slate-50 px-2 py-1 rounded"
                            data-testid="plan-magic-link"
                        >
                            {magicLink}
                        </code>
                        <button
                            type="button"
                            onClick={copyLink}
                            data-testid="plan-copy-link"
                            className="inline-flex items-center gap-1 rounded-full bg-shariti-black text-white px-3 h-8 text-[11px] font-bold hover:bg-slate-800 transition shrink-0"
                        >
                            <Copy className="h-3 w-3" /> Copier
                        </button>
                    </div>
                </div>
            )}

            {/* Notes */}
            {candidate.immersion_notes && (
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 mb-4">
                    <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1">Notes</div>
                    <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                        {candidate.immersion_notes}
                    </div>
                </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
                {/* When the immersion is already past, the prominent action is
                    to decide its outcome (Validated / Rejected). The
                    Reprogrammer / Annuler stay available below as fallback. */}
                {isPast && onDecide && (
                    <>
                        <button
                            type="button"
                            onClick={() => onDecide()}
                            data-testid="plan-decide-validate"
                            className="inline-flex items-center gap-2 rounded-full bg-emerald-500 text-white px-5 h-10 font-bold text-sm hover:bg-emerald-600 transition shadow-btn"
                        >
                            <ThumbsUp className="h-4 w-4" /> Immersion réussie
                        </button>
                        <button
                            type="button"
                            onClick={() => onDecide()}
                            data-testid="plan-decide-reject"
                            className="inline-flex items-center gap-2 rounded-full bg-shariti-red text-white px-5 h-10 font-bold text-sm hover:bg-red-700 transition shadow-btn"
                        >
                            <ThumbsDown className="h-4 w-4" /> Refuser
                        </button>
                    </>
                )}
                <button
                    type="button"
                    onClick={onReschedule}
                    data-testid="plan-reschedule"
                    className="inline-flex items-center gap-2 rounded-full bg-shariti-orange text-white px-5 h-10 font-bold text-sm hover:bg-shariti-orange-dark transition shadow-btn"
                >
                    <RefreshCw className="h-4 w-4" /> Reprogrammer
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    data-testid="plan-cancel"
                    className="inline-flex items-center gap-2 rounded-full bg-white border border-red-200 text-shariti-red hover:bg-red-50 px-5 h-10 font-bold text-sm transition"
                >
                    <XCircle className="h-4 w-4" /> Annuler l&apos;immersion
                </button>
            </div>

            {/* Past-immersion subtle banner — explains why the new buttons appear */}
            {isPast && onDecide && (
                <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-2.5 text-[11px] font-medium text-amber-900" data-testid="plan-past-banner">
                    L&apos;immersion est passée. Validez ou refusez le candidat pour acter le résultat dans le calendrier et l&apos;historique.
                </div>
            )}
        </motion.div>
    );
}


function DetailRow({ icon: Icon, label, value, testId }) {
    return (
        <div className="rounded-xl bg-white border border-shariti-gray p-3" data-testid={testId}>
            <div className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1">
                <Icon className="h-3 w-3" /> {label}
            </div>
            <div className="text-sm font-bold text-shariti-black leading-snug">
                {value}
            </div>
        </div>
    );
}
